
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Quote} from './Quote';
import {FieldModelQuote} from './Quote';

/**
 * Tick struct
 */
class Tick {
  /**
   * Initialize struct
   * @param {!Date=} time
   * @param {Quote=} bid
   * @param {Quote=} ask
   * @constructor
   */
  constructor (time = new Date(0), bid = null, ask = null) {
    this.Time = time
    this.Bid = bid
    this.Ask = ask
  }

  /**
   * Copy struct (shallow copy)
   * @this {!Tick}
   * @param {!Tick} other Other struct
   * @returns {!Tick} This struct
   */
  copy (other) {
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    if (other.Bid != null) {
      this.Bid = Quote.fromObject(other.Bid)
    } else {
      this.Bid = null
    }
    if (other.Ask != null) {
      this.Ask = Quote.fromObject(other.Ask)
    } else {
      this.Ask = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!Tick}
   * @returns {!Tick} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TickModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TickModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!Tick}
   * @param {!Tick} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof Tick)) {
      throw new TypeError('Instance of Tick is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!Tick}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null),
      Bid: ((this.Bid != null) ? this.Bid : null),
      Ask: ((this.Ask != null) ? this.Ask : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return Tick.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!Tick} other Object value
   * @returns {!Tick} Created struct
   */
  static fromObject (other) {
    return new Tick().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!Tick}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return Tick.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!Tick}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 30
  }
}

export { Tick };

/**
 * Fast Binary Encoding Tick field model
 */
class FieldModelTick extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Time = new fbe.FieldModelTimestamp(buffer, 4 + 4)
    this.Bid = new fbe.FieldModelOptional(new FieldModelQuote(buffer, this.Time.fbeOffset + this.Time.fbeSize), buffer, this.Time.fbeOffset + this.Time.fbeSize)
    this.Ask = new fbe.FieldModelOptional(new FieldModelQuote(buffer, this.Bid.fbeOffset + this.Bid.fbeSize), buffer, this.Bid.fbeOffset + this.Bid.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTick}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTick}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Time.fbeSize + this.Bid.fbeSize + this.Ask.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTick}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Time.fbeExtra + this.Bid.fbeExtra + this.Ask.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTick}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTick.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTick}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 30
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTick}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTick}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.Bid.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Bid.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Bid.fbeSize

    if ((fbeCurrentSize + this.Ask.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Ask.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ask.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTick}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTick}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTick}
   * @param {!Tick} fbeValue Default value, defaults is new Tick()
   * @returns {!Tick} Tick value
   */
  get (fbeValue = new Tick()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTick}
   * @param {!Tick} fbeValue Tick value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.Bid.fbeSize) <= fbeStructSize) {
      fbeValue.Bid = this.Bid.get()
    } else {
      fbeValue.Bid = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Bid.fbeSize

    if ((fbeCurrentSize + this.Ask.fbeSize) <= fbeStructSize) {
      fbeValue.Ask = this.Ask.get()
    } else {
      fbeValue.Ask = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ask.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTick}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTick}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTick}
   * @param {!Tick} fbeValue Tick value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTick}
   * @param {Tick} fbeValue Tick value
   */
  setFields (fbeValue) {
    this.Time.set(fbeValue.Time)
    this.Bid.set(fbeValue.Bid)
    this.Ask.set(fbeValue.Ask)
  }
}

export { FieldModelTick };

/**
 * Fast Binary Encoding Tick model
 */
class TickModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTick(this.buffer, 4)
  }

  /**
   * Get the Tick model
   * @this {!TickModel}
   * @returns {!FieldModelTick} model Tick model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TickModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TickModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TickModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TickModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTick.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TickModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TickModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TickModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TickModel}
   * @param {!Tick} value Tick value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TickModel}
   * @param {!Tick} value Tick value, defaults is new Tick()
   * @return {!object} Deserialized Tick value and its size
   */
  deserialize (value = new Tick()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new Tick(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new Tick(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TickModel}
   * @param {!number} prev Previous Tick model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TickModel };
